import { apiService } from './api';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'critical';
  category: 'security' | 'system' | 'compliance' | 'general';
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
  actionUrl?: string;
  metadata?: Record<string, any>;
}

export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
  inApp: boolean;
  categories: {
    security: boolean;
    system: boolean;
    compliance: boolean;
    general: boolean;
  };
  severityThreshold: 'all' | 'medium' | 'high' | 'critical';
}

export interface NotificationRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  condition: {
    type: 'vulnerability_found' | 'compliance_change' | 'threat_detected' | 'scan_completed';
    severity?: 'low' | 'medium' | 'high' | 'critical';
    threshold?: number;
  };
  action: {
    channels: ('email' | 'push' | 'sms' | 'inApp')[];
    recipients: string[];
    template: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

class NotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notification: Notification) => void)[] = [];

  // Notifications
  async getNotifications(filters?: {
    isRead?: boolean;
    type?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    data: Notification[];
    meta?: { unreadCount?: number };
    pagination?: any;
  }> {
    const response = await apiService.get<{
      data: Notification[];
      meta?: { unreadCount?: number };
      pagination?: any;
    }>('/notifications', {
      params: filters,
    });
    this.notifications = response.data;
    return response;
  }

  async getNotification(id: string): Promise<Notification> {
    return await apiService.get<Notification>(`/notifications/${id}`);
  }

  async markAsRead(id: string): Promise<void> {
    await apiService.patch(`/notifications/${id}`, { isRead: true });
    
    // Update local cache
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.isRead = true;
      notification.readAt = new Date();
    }
  }

  async markAllAsRead(): Promise<void> {
    await apiService.post('/notifications/mark-all-read');
    
    // Update local cache
    this.notifications.forEach(n => {
      n.isRead = true;
      n.readAt = new Date();
    });
  }

  async archiveNotification(id: string): Promise<void> {
    await apiService.patch(`/notifications/${id}/archive`);
    
    // Update local cache
    this.notifications = this.notifications.filter(n => n.id !== id);
  }

  async deleteNotification(id: string): Promise<void> {
    await apiService.delete(`/notifications/${id}`);
    
    // Update local cache
    this.notifications = this.notifications.filter(n => n.id !== id);
  }

  async clearAll(): Promise<void> {
    await apiService.delete('/notifications');
    this.notifications = [];
  }

  // Preferences
  async getPreferences(): Promise<{ data: NotificationPreferences }> {
    return await apiService.get<{ data: NotificationPreferences }>('/notifications/preferences');
  }

  async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<{ data: NotificationPreferences }> {
    return await apiService.put<{ data: NotificationPreferences }>('/notifications/preferences', preferences);
  }

  // Rules
  async getRules(): Promise<NotificationRule[]> {
    return await apiService.get<NotificationRule[]>('/notifications/rules');
  }

  async getRule(id: string): Promise<NotificationRule> {
    return await apiService.get<NotificationRule>(`/notifications/rules/${id}`);
  }

  async createRule(rule: Omit<NotificationRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<NotificationRule> {
    return await apiService.post<NotificationRule>('/notifications/rules', rule);
  }

  async updateRule(id: string, rule: Partial<NotificationRule>): Promise<NotificationRule> {
    return await apiService.put<NotificationRule>(`/notifications/rules/${id}`, rule);
  }

  async deleteRule(id: string): Promise<void> {
    await apiService.delete(`/notifications/rules/${id}`);
  }

  async toggleRule(id: string, enabled: boolean): Promise<void> {
    await apiService.patch(`/notifications/rules/${id}`, { enabled });
  }

  // Real-time notifications
  subscribeToNotifications(callback: (notification: Notification) => void): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(notification: Notification): void {
    this.listeners.forEach(callback => callback(notification));
  }

  // WebSocket connection for real-time notifications
  connectWebSocket(): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000';
    const ws = new WebSocket(`${wsUrl}/notifications`);

    ws.onmessage = (event) => {
      const notification: Notification = JSON.parse(event.data);
      this.notifications.unshift(notification);
      this.notifyListeners(notification);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      // Attempt to reconnect after 5 seconds
      setTimeout(() => this.connectWebSocket(), 5000);
    };
  }

  // Utility methods
  getUnreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  getCriticalNotifications(): Notification[] {
    return this.notifications.filter(n => n.type === 'critical' && !n.isRead);
  }

  async testNotification(type: Notification['type']): Promise<void> {
    await apiService.post('/notifications/test', { type });
  }
}

export const notificationService = new NotificationService();